import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Lightbulb, Tag, Shield, Search } from 'lucide-react';
import { Input } from '../../components/ui/input';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function TacticCard({ tactic }) {
  const getTagColor = (tag) => {
    if (tag.includes('pacing')) return 'bg-blue-100 text-blue-700';
    if (tag.includes('trust')) return 'bg-emerald-100 text-emerald-700';
    if (tag.includes('pressure')) return 'bg-red-100 text-red-700';
    if (tag.includes('info')) return 'bg-purple-100 text-purple-700';
    if (tag.includes('decision')) return 'bg-amber-100 text-amber-700';
    if (tag.includes('control')) return 'bg-teal-100 text-teal-700';
    if (tag.includes('confusion')) return 'bg-orange-100 text-orange-700';
    if (tag.includes('primary')) return 'bg-pink-100 text-pink-700';
    return 'bg-slate-100 text-slate-700';
  };

  const formatGuardrail = (guardrail) => {
    if (guardrail.includes('high_D')) return 'Høj D';
    if (guardrail.includes('high_I')) return 'Høj I';
    if (guardrail.includes('high_S')) return 'Høj S';
    if (guardrail.includes('high_C')) return 'Høj C';
    return guardrail;
  };

  const tags = tactic.tags || [];
  const guardrails = tactic.disc_guardrails || [];

  return (
    <Card className="border-slate-200 hover:shadow-md transition-shadow">
      <CardContent className="pt-5">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-slate-900 text-white font-bold">
            {tactic.id}
          </div>
          
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 mb-3">
              <Lightbulb className="h-5 w-5 text-amber-500 mt-0.5 flex-shrink-0" />
              <p className="text-slate-900 leading-relaxed">
                {tactic.tip_template}
              </p>
            </div>
            
            <div className="flex flex-wrap gap-2 mb-3">
              <Tag className="h-4 w-4 text-slate-400" />
              {tags.map((tag, tagIndex) => (
                <Badge 
                  key={tagIndex} 
                  className={`text-xs font-normal ${getTagColor(tag)}`}
                >
                  {tag}
                </Badge>
              ))}
            </div>
            
            {guardrails.length > 0 && (
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-slate-400" />
                <span className="text-xs text-slate-500">DISC-regler:</span>
                {guardrails.map((guardrail, gIndex) => (
                  <Badge 
                    key={gIndex} 
                    variant="outline"
                    className="text-xs border-slate-300"
                  >
                    {formatGuardrail(guardrail)}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TacticsList({ tactics, searchTerm }) {
  const filtered = tactics.filter(tactic => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    const matchesTip = tactic.tip_template.toLowerCase().includes(term);
    const matchesTags = (tactic.tags || []).some(tag => tag.toLowerCase().includes(term));
    return matchesTip || matchesTags;
  });

  if (filtered.length === 0) {
    return (
      <Card className="border-slate-200">
        <CardContent className="py-8 text-center">
          <p className="text-slate-500">Ingen taktikker matcher din søgning</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {filtered.map((tactic) => (
        <TacticCard key={tactic.id} tactic={tactic} />
      ))}
    </div>
  );
}

const TacticsPage = () => {
  const [tactics, setTactics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const loadTactics = useCallback(async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.get(`${API}/api/admin/tactics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTactics(response.data.tactics || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Kunne ikke hente taktikker');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTactics();
  }, [loadTactics]);

  if (loading) {
    return (
      <div className="max-w-4xl">
        <div className="mb-8">
          <Skeleton className="h-8 w-48 mb-2" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
          <Skeleton className="h-32" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6 text-center">
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filteredCount = tactics.filter(tactic => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return tactic.tip_template.toLowerCase().includes(term) || 
           (tactic.tags || []).some(tag => tag.toLowerCase().includes(term));
  }).length;

  return (
    <div className="max-w-4xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-900 font-outfit">Taktik-bibliotek</h1>
        <p className="text-slate-500 mt-1">
          {tactics.length} taktikker til brug i AI-coaching
        </p>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Søg i taktikker eller tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="tactics-search"
          />
        </div>
      </div>

      {searchTerm && (
        <p className="text-sm text-slate-500 mb-4">
          Viser {filteredCount} af {tactics.length} taktikker
        </p>
      )}

      <TacticsList tactics={tactics} searchTerm={searchTerm} />

      <Card className="mt-8 border-slate-200 bg-slate-50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-slate-700">Tag-forklaring</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div className="flex items-center gap-2">
              <Badge className="bg-blue-100 text-blue-700">pacing_*</Badge>
              <span className="text-slate-500">Tempo</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-emerald-100 text-emerald-700">trust_*</Badge>
              <span className="text-slate-500">Tillid</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-red-100 text-red-700">pressure_*</Badge>
              <span className="text-slate-500">Pres</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-purple-100 text-purple-700">info_*</Badge>
              <span className="text-slate-500">Information</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-amber-100 text-amber-700">decision_*</Badge>
              <span className="text-slate-500">Beslutning</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-teal-100 text-teal-700">control_*</Badge>
              <span className="text-slate-500">Kontrol</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-orange-100 text-orange-700">confusion_*</Badge>
              <span className="text-slate-500">Forvirring</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-pink-100 text-pink-700">primary_*</Badge>
              <span className="text-slate-500">Primær værdi</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TacticsPage;
